import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle, FilePdf, LinkSimple, MagicWand, House } from "@phosphor-icons/react";

/**
 * RecentInspectionsStrip — portfolio-wide feed of the most recently
 * completed inspections across every property. Sits at the top of the
 * Properties list page so PMs can jump straight to the fresh reports.
 * Auto-hides when there are none.
 */
export default function RecentInspectionsStrip({ limit = 8 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/prop-inspections/recent-completed", {
          params: { limit },
        });
        if (alive) setItems(data?.items || []);
      } catch {
        if (alive) setItems([]);
      }
    })();
    return () => { alive = false; };
  }, [limit]);

  if (!items || items.length === 0) return null;

  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const openReport = (iid) => {
    const t = localStorage.getItem("access_token") || "";
    window.open(`${base}/api/prop-inspections/${iid}/report.pdf?auth=${encodeURIComponent(t)}`, "_blank");
  };
  const copyLink = async (shareToken) => {
    const url = `${window.location.origin}/public/inspection/${shareToken}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch {
      toast.error("Copy failed — long-press to grab manually");
    }
  };

  return (
    <div
      data-testid="recent-inspections-strip"
      className="mt-5 rounded-2xl bg-gradient-to-br from-[#0F1F3A] to-[#1a2f52] text-white p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-3">
        <MagicWand size={16} weight="fill" className="text-[#00C4CC]" />
        <h2 className="font-black text-base uppercase tracking-tight">Recent completed inspections</h2>
        <span className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] bg-[#00C4CC]/10 px-1.5 py-0.5 rounded">
          {items.length}
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {items.map((i) => {
          const when = i.completed_at ? new Date(i.completed_at) : null;
          const whenLabel = when
            ? when.toLocaleDateString(undefined, { month: "short", day: "numeric" })
            : "—";
          return (
            <div
              key={i.inspection_id}
              data-testid={`recent-inspection-${i.inspection_id}`}
              className="snap-start flex-shrink-0 w-64 rounded-xl bg-white text-[#0F1F3A] overflow-hidden hover:ring-2 hover:ring-[#00C4CC] transition"
            >
              <Link to={`/admin/inspections/${i.inspection_id}`} className="block p-3 pb-2">
                <div className="flex items-center gap-1.5">
                  <CheckCircle size={13} weight="fill" className="text-emerald-500 flex-shrink-0" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600">
                    v{i.report_version || 1}
                  </span>
                  <span className="ml-auto text-[10px] font-medium text-[#8B93A7]">{whenLabel}</span>
                </div>
                <div className="mt-1.5 text-[13px] font-black leading-snug flex items-start gap-1">
                  <House size={13} weight="fill" className="text-[#00C4CC] mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">
                    {i.address || i.title}
                    {i.unit ? ` · Unit ${i.unit}` : ""}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-[#8B93A7] font-medium">
                  {i.photos_count || 0} photos
                  {(i.recommendations_count || 0) > 0 && ` · ${i.recommendations_count} recs`}
                  {(i.findings_count || 0) > 0 && ` · ${i.findings_count} issues`}
                </div>
              </Link>
              <div className="border-t border-slate-100 flex">
                <button
                  onClick={() => openReport(i.inspection_id)}
                  data-testid={`recent-inspection-pdf-${i.inspection_id}`}
                  className="flex-1 h-9 flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition"
                >
                  <FilePdf size={12} weight="fill" /> PDF
                </button>
                <div className="w-px bg-slate-100" />
                <button
                  onClick={() => copyLink(i.share_token)}
                  data-testid={`recent-inspection-link-${i.inspection_id}`}
                  className="flex-1 h-9 flex items-center justify-center gap-1 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition"
                >
                  <LinkSimple size={12} weight="bold" /> Link
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
